import React, { useState } from 'react';
import { 
  Edit, 
  Mail, 
  CheckCircle2, 
  ArrowRight
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;


export default function InformationsTab({ pharmacy, users, rolesSummary, currentUser, onRefresh }) {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const openEditModal = () => {
    setFormData({
      name: pharmacy?.name || '',
      address: pharmacy?.address || '',
      phone: pharmacy?.phone || '',
      email: pharmacy?.email || ''
    });
    setIsEditModalOpen(true);
  };

  const handleSave = async () => {
    try {
      setIsSubmitting(true);
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      await axios.put(`${API}/pharmacies/${pharmacy.id}`, formData, { headers });
      toast.success('Pharmacie modifiée avec succès');
      setIsEditModalOpen(false);
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error(err);
      toast.error('Erreur lors de la modification');
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Data Preparation ---
  const activeModules = [
    'Revenus', 'Sécurité Sociale',
    'Dépenses', 'Paramètres Techniques',
    'Clôture Caisse', 'Factures & BL',
    'Trésorerie', 'Analyses',
    'Fournisseurs', 'Utilisateurs'
  ];

  const adminCount = rolesSummary?.find(r => r.role === 'admin' || r.role === 'super_admin')?.count || 0;
  const gerantCount = rolesSummary?.find(r => r.role === 'gerant')?.count || 0;
  const caissierCount = rolesSummary?.find(r => r.role === 'caissier')?.count || 0;
  const assistantCount = rolesSummary?.find(r => r.role === 'assistant')?.count || 0;
  
  const totalUsers = users?.length || 0;

  const chartData = [
    { name: 'Administrateur', value: adminCount, color: '#10b981' }, // emerald-500
    { name: 'Gestionnaires', value: gerantCount, color: '#8b5cf6' }, // violet-500
    { name: 'Caissiers', value: caissierCount, color: '#3b82f6' }, // blue-500
    { name: 'Assistants', value: assistantCount, color: '#f59e0b' }, // amber-500
    { name: 'Inactifs', value: 0, color: '#64748b' }, // slate-500
  ].filter(d => d.value > 0 || d.name === 'Inactifs'); // Keep inactifs for the legend if needed

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.substring(0, 2).toUpperCase();
  };

  const adminUser = users?.find(u => u.role === 'admin' || u.role === 'super_admin') || currentUser;

  return (
    <div className="p-6 overflow-y-auto h-full bg-slate-50">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-7xl mx-auto pb-12">
        
        {/* ROW 1 */}
        {/* Informations de la pharmacie */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-bold text-slate-900 text-lg">Informations de la pharmacie</h3>
            <button onClick={openEditModal} className="text-slate-700 bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center transition-colors">
              <Edit size={14} className="mr-1.5" />
              Éditer
            </button>
          </div>

          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-3">
              <span className="text-slate-500">Nom</span>
              <span className="col-span-2 font-medium text-slate-900">{pharmacy?.name || 'Pharmacie Bab El Rahma'}</span>
            </div>
            <div className="grid grid-cols-3">
              <span className="text-slate-500">Adresse</span>
              <span className="col-span-2 font-medium text-slate-900">{pharmacy?.address || 'Bab El Rahma, Alger, Algérie'}</span>
            </div>
            <div className="grid grid-cols-3">
              <span className="text-slate-500">Téléphone</span>
              <span className="col-span-2 font-medium text-slate-900">{pharmacy?.phone || '+213 555 12 34 56'}</span>
            </div>
            <div className="grid grid-cols-3">
              <span className="text-slate-500">Email</span>
              <span className="col-span-2 font-medium text-slate-900">{pharmacy?.email || 'contact@babelrahma.dz'}</span>
            </div>
            <div className="grid grid-cols-3">
              <span className="text-slate-500">Créée le</span>
              <span className="col-span-2 font-medium text-slate-900">12 mars 2024</span>
            </div>
            <div className="grid grid-cols-3 items-center">
              <span className="text-slate-500">Statut</span>
              <div className="col-span-2">
                <span className="px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-md text-xs font-semibold">Active</span>
              </div>
            </div>
            <div className="grid grid-cols-3 items-center">
              <span className="text-slate-500">Abonnement</span>
              <div className="col-span-2">
                <span className="px-2.5 py-1 bg-purple-100 text-purple-700 rounded-md text-xs font-semibold">Premium</span>
              </div>
            </div>
            <div className="grid grid-cols-3">
              <span className="text-slate-500">Expiration</span>
              <span className="col-span-2 font-medium text-slate-900">12 mars 2026</span>
            </div>
            
            <div className="pt-4 mt-2 border-t border-slate-100 grid grid-cols-3 items-center">
              <span className="text-slate-500">Responsable</span>
              <div className="col-span-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold text-xs">
                    {getInitials(adminUser?.username || adminUser?.full_name)}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-900 leading-tight">{adminUser?.full_name || adminUser?.username || 'Responsable'}</p>
                    <p className="text-xs text-slate-500">{adminUser?.email || 'admin@pharma.dz'}</p>
                  </div>
                </div>
                <button className="w-8 h-8 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-500 hover:text-slate-900 transition-colors">
                  <Mail size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Activité récente */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <h3 className="font-bold text-slate-900 text-lg mb-6">Activité récente</h3>
          
          <div className="space-y-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 mt-1"></div>
                <span className="text-sm font-medium text-slate-700">Dernière connexion</span>
              </div>
              <span className="text-sm font-bold text-slate-900">Aujourd'hui à 09:12</span>
            </div>
            
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-2.5 h-2.5 rounded-full bg-blue-500 mt-1"></div>
                <span className="text-sm font-medium text-slate-700">Dernière activité</span>
              </div>
              <span className="text-sm font-bold text-slate-900 text-right">Modification des ventes</span>
            </div>

            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-2.5 h-2.5 rounded-full bg-purple-500 mt-1"></div>
                <span className="text-sm font-medium text-slate-700">Dernière clôture caisse</span>
              </div>
              <span className="text-sm font-bold text-slate-900">Hier à 20:30</span>
            </div>

            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-2.5 h-2.5 rounded-full bg-amber-500 mt-1"></div>
                <span className="text-sm font-medium text-slate-700">Dernier export</span>
              </div>
              <span className="text-sm font-bold text-slate-900 text-right">PDF Revenus - 09 mai 2026</span>
            </div>
          </div>
        </div>

        {/* Statistiques utilisateurs */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm flex flex-col">
          <h3 className="font-bold text-slate-900 text-lg mb-2">Statistiques utilisateurs</h3>
          
          <div className="flex items-center justify-between flex-1 mt-4">
            <div className="w-1/2 h-40 relative">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={70}
                    paddingAngle={2}
                    dataKey="value"
                    stroke="none"
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-2xl font-bold text-slate-900">{totalUsers}</span>
                <span className="text-xs font-semibold text-slate-500">Utilisateurs</span>
              </div>
            </div>

            <div className="w-1/2 pl-4 space-y-3">
              {chartData.map((entry, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: entry.color }}></div>
                    <span className="text-xs font-bold text-slate-900">{entry.value}</span>
                    <span className="text-xs font-medium text-slate-600">{entry.name}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>


        {/* ROW 2 */}
        {/* Modules activés */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <h3 className="font-bold text-slate-900 text-lg mb-6">Modules activés</h3>
          <div className="grid grid-cols-2 gap-y-4 gap-x-2">
            {activeModules.map((mod, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <CheckCircle2 size={16} className="text-emerald-500 shrink-0" />
                <span className="text-sm font-medium text-slate-700 truncate">{mod}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Sécurité */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <h3 className="font-bold text-slate-900 text-lg mb-6">Sécurité</h3>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">Authentification 2FA</span>
              <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs font-bold">Activée</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">Dernier changement mot de passe</span>
              <span className="text-sm font-bold text-slate-900">Il y a 14 jours</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">Tentatives de connexion échouées</span>
              <span className="text-sm font-bold text-slate-900">0</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">Sessions actives</span>
              <span className="text-sm font-bold text-slate-900">3</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">Dispositifs enregistrés</span>
              <span className="text-sm font-bold text-slate-900">2</span>
            </div>
          </div>
        </div>

        {/* Santé du compte */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <h3 className="font-bold text-slate-900 text-lg mb-6">Santé du compte</h3>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">Activité ce mois</span>
              <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs font-bold">Élevée</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">Connexions utilisateurs</span>
              <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs font-bold">Stable</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">Erreurs système</span>
              <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs font-bold">0</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">Synchronisation des données</span>
              <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs font-bold">OK</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">Sauvegarde automatique</span>
              <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs font-bold">Activée</span>
            </div>
          </div>
        </div>

        {/* ROW 3 */}
        {/* Historique administratif */}
        <div className="md:col-span-2 bg-white rounded-2xl border border-slate-200 p-6 shadow-sm flex flex-col">
          <h3 className="font-bold text-slate-900 text-lg mb-6">Historique administratif</h3>
          
          <div className="flex-1 overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[500px]">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="pb-3 text-xs font-bold text-slate-500 uppercase">Date</th>
                  <th className="pb-3 text-xs font-bold text-slate-500 uppercase">Action</th>
                  <th className="pb-3 text-xs font-bold text-slate-500 uppercase">Détails</th>
                  <th className="pb-3 text-xs font-bold text-slate-500 uppercase text-right">Utilisateur</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-slate-50">
                  <td className="py-3 font-medium text-slate-700">12 mai 2026 à 09:30</td>
                  <td className="py-3 font-bold text-slate-900">Ajout utilisateur</td>
                  <td className="py-3 text-slate-600">Ajout de l'utilisateur Hmed (Caissier)</td>
                  <td className="py-3">
                    <div className="flex justify-end items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold text-[10px]">S</div>
                      <span className="font-medium text-slate-900">superadmin</span>
                    </div>
                  </td>
                </tr>
                <tr className="border-b border-slate-50">
                  <td className="py-3 font-medium text-slate-700">10 mai 2026 à 16:45</td>
                  <td className="py-3 font-bold text-slate-900">Changement de rôle</td>
                  <td className="py-3 text-slate-600">Redouane est maintenant Gestionnaire</td>
                  <td className="py-3">
                    <div className="flex justify-end items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold text-[10px]">S</div>
                      <span className="font-medium text-slate-900">superadmin</span>
                    </div>
                  </td>
                </tr>
                <tr className="border-b border-slate-50">
                  <td className="py-3 font-medium text-slate-700">08 mai 2026 à 11:20</td>
                  <td className="py-3 font-bold text-slate-900">Modification pharmacie</td>
                  <td className="py-3 text-slate-600">Mise à jour des informations de la pharmacie</td>
                  <td className="py-3">
                    <div className="flex justify-end items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold text-[10px]">S</div>
                      <span className="font-medium text-slate-900">superadmin</span>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="py-3 font-medium text-slate-700">01 mai 2026 à 10:05</td>
                  <td className="py-3 font-bold text-slate-900">Activation abonnement</td>
                  <td className="py-3 text-slate-600">Passage à l'abonnement Premium</td>
                  <td className="py-3">
                    <div className="flex justify-end items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold text-[10px]">S</div>
                      <span className="font-medium text-slate-900">superadmin</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div className="mt-4 flex justify-end">
            <button className="flex items-center gap-2 text-sm font-bold text-slate-900 bg-white border border-slate-200 hover:bg-slate-50 px-4 py-2 rounded-xl transition-colors">
              Voir tout l'historique
              <ArrowRight size={16} />
            </button>
          </div>
        </div>

        {/* Informations système */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <h3 className="font-bold text-slate-900 text-lg mb-6">Informations système</h3>
          
          <div className="space-y-5">
            <div className="flex items-center justify-between border-b border-slate-50 pb-3">
              <span className="text-sm font-medium text-slate-500">ID Pharmacie</span>
              <span className="text-sm font-bold text-slate-900">{pharmacy?.id?.substring(0,10).toUpperCase() || 'PHAR-00012'}</span>
            </div>
            <div className="flex items-center justify-between border-b border-slate-50 pb-3">
              <span className="text-sm font-medium text-slate-500">Fuseau horaire</span>
              <span className="text-sm font-bold text-slate-900">Africa/Alger</span>
            </div>
            <div className="flex items-center justify-between border-b border-slate-50 pb-3">
              <span className="text-sm font-medium text-slate-500">Langue par défaut</span>
              <span className="text-sm font-bold text-slate-900">Français</span>
            </div>
            <div className="flex items-center justify-between border-b border-slate-50 pb-3">
              <span className="text-sm font-medium text-slate-500">Devise</span>
              <span className="text-sm font-bold text-slate-900">DZD (DA)</span>
            </div>
            <div className="flex items-center justify-between border-b border-slate-50 pb-3">
              <span className="text-sm font-medium text-slate-500">Version de la plateforme</span>
              <span className="text-sm font-bold text-slate-900">v2.4.0</span>
            </div>
            <div className="flex items-center justify-between pt-1">
              <span className="text-sm font-medium text-slate-500">Dernière mise à jour</span>
              <span className="text-sm font-bold text-slate-900">10 mai 2026 à 14:22</span>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Modifier la pharmacie</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right">
                Nom
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="address" className="text-right">
                Adresse
              </Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="phone" className="text-right">
                Téléphone
              </Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="email" className="text-right">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="col-span-3"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleSave} disabled={isSubmitting} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
